import React from 'react';
import SEO from '../components/SEO';

const SpringBootMicroservices: React.FC = () => {
  return (
    <>
      <SEO 
        title="Spring Boot and Microservices Training | Java Microservices Course - SBRIT Solution"
        description="Build scalable microservices applications with Spring Boot framework. Learn microservices architecture, Spring Cloud, and cloud-native development."
        keywords="spring boot training, microservices course, spring cloud, java microservices, spring framework, cloud native development, microservices architecture"
        url="https://sbritsolution.in/courses/spring-boot-microservices"
        type="article"
      />
      
      <div className="min-h-screen bg-gray-50">
        {/* Hero Section */}
        <div className="bg-gradient-to-r from-green-600 to-blue-600 text-white py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-4xl md:text-6xl font-bold mb-6">
                Spring Boot and Microservices
              </h1>
              <p className="text-xl md:text-2xl mb-8 leading-relaxed">
                Build Scalable Applications | Microservices Architecture | Spring Cloud | Cloud-Native Development
              </p>
              <div className="flex flex-col md:flex-row gap-4 justify-center">
                <button className="bg-yellow-500 hover:bg-yellow-600 text-black px-8 py-4 rounded-lg font-semibold text-lg transition-colors">
                  Start Learning - ₹22,999
                </button>
                <button className="border-2 border-white hover:bg-white hover:text-green-600 px-8 py-4 rounded-lg font-semibold text-lg transition-colors">
                  Download Syllabus
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Why Spring Boot & Microservices */}
        <div className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center mb-12">Why Learn Spring Boot & Microservices?</h2>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center p-6 rounded-lg shadow-lg">
                <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">☘️</span>
                </div>
                <h3 className="text-xl font-semibold mb-3">Industry Standard</h3>
                <p className="text-gray-600">Spring Boot is the most popular Java framework for building enterprise applications.</p>
              </div>
              <div className="text-center p-6 rounded-lg shadow-lg">
                <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">🏗️</span>
                </div>
                <h3 className="text-xl font-semibold mb-3">Scalable Architecture</h3>
                <p className="text-gray-600">Microservices enable building scalable, maintainable, and resilient applications.</p>
              </div>
              <div className="text-center p-6 rounded-lg shadow-lg">
                <div className="bg-purple-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">💼</span>
                </div>
                <h3 className="text-xl font-semibold mb-3">High Demand</h3>
                <p className="text-gray-600">Spring Boot and microservices developers are in high demand with excellent salaries.</p>
              </div>
            </div>
          </div>
        </div>

        {/* Spring Boot & Microservices Curriculum */}
        <div className="py-16 bg-gray-50">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center mb-12">Complete Spring Boot & Microservices Curriculum</h2>
            <div className="max-w-6xl mx-auto">
              <div className="grid md:grid-cols-2 gap-8">
                <div className="bg-white p-6 rounded-lg shadow-lg">
                  <h3 className="text-xl font-semibold mb-4 text-green-600">Spring Framework</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li>• Spring Core Concepts</li>
                    <li>• Dependency Injection</li>
                    <li>• Spring AOP</li>
                    <li>• Spring Data JPA</li>
                    <li>• Spring Security</li>
                    <li>• Spring MVC</li>
                    <li>• Spring Testing</li>
                    <li>• Spring Configuration</li>
                  </ul>
                </div>
                <div className="bg-white p-6 rounded-lg shadow-lg">
                  <h3 className="text-xl font-semibold mb-4 text-blue-600">Spring Boot Basics</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li>• Spring Boot Introduction</li>
                    <li>• Auto Configuration</li>
                    <li>• Spring Boot Starters</li>
                    <li>• Application Properties</li>
                    <li>• Embedded Servers</li>
                    <li>• Actuator & Monitoring</li>
                    <li>• Spring Boot Testing</li>
                    <li>• Deployment & Packaging</li>
                  </ul>
                </div>
                <div className="bg-white p-6 rounded-lg shadow-lg">
                  <h3 className="text-xl font-semibold mb-4 text-purple-600">Microservices Architecture</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li>• Microservices Fundamentals</li>
                    <li>• Service Decomposition</li>
                    <li>• API Design & REST</li>
                    <li>• Service Communication</li>
                    <li>• Data Management</li>
                    <li>• Fault Tolerance</li>
                    <li>• Security in Microservices</li>
                    <li>• Testing Strategies</li>
                  </ul>
                </div>
                <div className="bg-white p-6 rounded-lg shadow-lg">
                  <h3 className="text-xl font-semibold mb-4 text-orange-600">Spring Cloud</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li>• Service Discovery (Eureka)</li>
                    <li>• API Gateway (Zuul)</li>
                    <li>• Circuit Breaker (Hystrix)</li>
                    <li>• Configuration Management</li>
                    <li>• Distributed Tracing</li>
                    <li>• Load Balancing</li>
                    <li>• Centralized Logging</li>
                    <li>• Monitoring & Metrics</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Tools & Technologies */}
        <div className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center mb-12">Tools & Technologies You'll Master</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="text-center p-6 rounded-lg shadow-lg border">
                <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">☘️</span>
                </div>
                <h3 className="text-lg font-semibold mb-2">Spring Boot</h3>
                <p className="text-gray-600 text-sm">Rapid application development framework</p>
              </div>
              <div className="text-center p-6 rounded-lg shadow-lg border">
                <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">☁️</span>
                </div>
                <h3 className="text-lg font-semibold mb-2">Spring Cloud</h3>
                <p className="text-gray-600 text-sm">Cloud-native application development</p>
              </div>
              <div className="text-center p-6 rounded-lg shadow-lg border">
                <div className="bg-purple-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">🗄️</span>
                </div>
                <h3 className="text-lg font-semibold mb-2">MySQL/PostgreSQL</h3>
                <p className="text-gray-600 text-sm">Relational database management</p>
              </div>
              <div className="text-center p-6 rounded-lg shadow-lg border">
                <div className="bg-orange-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">🐳</span>
                </div>
                <h3 className="text-lg font-semibold mb-2">Docker</h3>
                <p className="text-gray-600 text-sm">Containerization and deployment</p>
              </div>
            </div>
          </div>
        </div>

        {/* Course Features */}
        <div className="py-16 bg-gray-50">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center mb-12">What You'll Get</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="text-center p-6 rounded-lg shadow-lg border">
                <div className="bg-green-100 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-xl">📚</span>
                </div>
                <h3 className="text-lg font-semibold mb-2">Comprehensive Content</h3>
                <p className="text-gray-600 text-sm">80+ hours of structured learning with hands-on projects</p>
              </div>
              <div className="text-center p-6 rounded-lg shadow-lg border">
                <div className="bg-blue-100 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-xl">💻</span>
                </div>
                <h3 className="text-lg font-semibold mb-2">Real Projects</h3>
                <p className="text-gray-600 text-sm">Build microservices applications from scratch</p>
              </div>
              <div className="text-center p-6 rounded-lg shadow-lg border">
                <div className="bg-purple-100 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-xl">🎓</span>
                </div>
                <h3 className="text-lg font-semibold mb-2">Certification</h3>
                <p className="text-gray-600 text-sm">Industry-recognized certificate upon completion</p>
              </div>
              <div className="text-center p-6 rounded-lg shadow-lg border">
                <div className="bg-orange-100 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-xl">👥</span>
                </div>
                <h3 className="text-lg font-semibold mb-2">Expert Support</h3>
                <p className="text-gray-600 text-sm">24/7 support from Spring Boot experts</p>
              </div>
            </div>
          </div>
        </div>

        {/* Career Opportunities */}
        <div className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center mb-12">Career Opportunities</h2>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-white p-6 rounded-lg shadow-lg text-center border">
                <h3 className="text-xl font-semibold mb-4 text-green-600">Java Developer</h3>
                <p className="text-gray-600 mb-4">Build enterprise applications with Spring Boot</p>
                <div className="text-sm text-gray-500">Average Salary: ₹6-12 LPA</div>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-lg text-center border">
                <h3 className="text-xl font-semibold mb-4 text-blue-600">Microservices Developer</h3>
                <p className="text-gray-600 mb-4">Design and implement microservices architecture</p>
                <div className="text-sm text-gray-500">Average Salary: ₹8-15 LPA</div>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-lg text-center border">
                <h3 className="text-xl font-semibold mb-4 text-purple-600">Backend Engineer</h3>
                <p className="text-gray-600 mb-4">Develop scalable backend systems and APIs</p>
                <div className="text-sm text-gray-500">Average Salary: ₹7-14 LPA</div>
              </div>
            </div>
          </div>
        </div>

        {/* Call to Action */}
        <div className="py-16 bg-gradient-to-r from-green-600 to-blue-600 text-white">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl font-bold mb-6">Ready to Build Scalable Applications?</h2>
            <p className="text-xl mb-8">Join thousands of developers building enterprise applications with Spring Boot</p>
            <div className="flex flex-col md:flex-row gap-4 justify-center">
              <button className="bg-yellow-500 hover:bg-yellow-600 text-black px-8 py-4 rounded-lg font-semibold text-lg transition-colors">
                Enroll Now - ₹22,999
              </button>
              <button className="border-2 border-white hover:bg-white hover:text-green-600 px-8 py-4 rounded-lg font-semibold text-lg transition-colors">
                Download Course Brochure
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SpringBootMicroservices;
